import { Suspense, lazy } from "react";
import { Styles } from "../styles/styles";
import {
  createBrowserRouter,
  RouterProvider,
  Navigate,
} from "react-router-dom";

import ProtectedRoute from "../common/ProtectedRoute";

import RootLayoutAdmin from "../pages/RootLayoutAdmin";
import ControlSettingsHome from "../pages/ControlSettings/Home";
import Root from "../pages/ControlSettings/root";

import SchoolAdmin from "../pages/SchoolAdmin";
import StudentManagementHome from "../pages/StudentsManagement/Home";
import StudentManagementRoot from "../pages/StudentsManagement/Root";
import ExamsManagementRoot from "../pages/ExamsManagement/Root";
import ExamsManagementHome from "../pages/ExamsManagement/Home";
import StaffManagementRoot from "../pages/StaffManagement/Root";
import StaffManagementHome from "../pages/StaffManagement/Home";

import { checkTokenExpiry, getCurrentUser } from "../service/authService";
import ReportCenterRoot from "../pages/ReportCenter/Root";
import ReportCenterHome from "../pages/ReportCenter/Home";
import FeeCollection from "../pages/ReportCenter/FeeCollection";
import FeeDues from "../pages/ReportCenter/FeeDues";
import NewAdmission from "../pages/StudentsManagement/NewAdmission";
import BulkUpdate from "../pages/StudentsManagement/BulkUpdate";
import IssueTC from "../pages/StudentsManagement/IssueTC";
import StudentScholarship from "../pages/StudentsManagement/StudentScholarship";
import ClassSectionStrength from "../pages/ReportCenter/ClassSectionStrength";
import TransportReport from "../pages/ReportCenter/TransportReport";
import BonafideReport from "../pages/ReportCenter/BonafideReport";
import WardCategoryReport from "../pages/ReportCenter/WardCategoryReport";

const Login = lazy(() => import("../pages/Login"));
const ErrorPage = lazy(() => import("../pages/ErrorPage"));

const StudentAdminData = lazy(
  () => import("../pages/StudentsManagement/StudentAdminData")
);

const TeachersAdminData = lazy(
  () => import("../pages/StaffManagement/Teachers/TeachersAdminData")
);

const ManageSubjects = lazy(
  () => import("../pages/ControlSettings/ManageSubjects")
);
const ManageTransport = lazy(
  () => import("../pages/ControlSettings/ManageTransport")
);
const ManageAttendance = lazy(
  () => import("../pages/ExamsManagement/ManageAttendance")
);

const ManageMarks = lazy(() => import("../pages/ExamsManagement/ManageMarks"));
const ManageFeeHeads = lazy(
  () => import("../pages/ControlSettings/ManageFeeHeads")
);

const AddExams = lazy(() => import("../pages/ExamsManagement/AddExams"));

const ScheduleExam = lazy(
  () => import("../pages/ExamsManagement/ScheduleExam")
);

const TeacherSubjectAssignment = lazy(
  () => import("../pages/StaffManagement/Teachers/TeacherSubjectAssignment")
);

const GetAdmitCard = lazy(
  () => import("../pages/ExamsManagement/GetAdmitCard")
);

const GenerateReportCard = lazy(
  () => import("../pages/ExamsManagement/GenerateReportCard")
);

const FeePaymentsScreen = lazy(() => import("../pages/FeePaymentsScreen"));

checkTokenExpiry();

const user = getCurrentUser();
const myRouter = createBrowserRouter([
  {
    path: "/",
    element: user ? (
      <Navigate to="/home" replace />
    ) : (
      <Navigate to="/login" replace />
    ),
  },
  {
    path: "/login",
    element: user ? <Navigate to="/home" replace /> : <Login />, // Redirect if logged in
  },
  {
    path: "home",
    element: <RootLayoutAdmin />,
    errorElement: <ErrorPage />,
    children: [
      {
        index: true,
        element: (
          // <SchoolAdmin />
          <ProtectedRoute
            allowedRoles={[
              "admin",
              "teacher",
              "student",
              "feesadmin",
              "schooladmin",
            ]}
          >
            <SchoolAdmin />
          </ProtectedRoute>
        ),
      },
      {
        path: "students-management",
        element: <StudentManagementRoot />,
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <StudentManagementHome />
              </ProtectedRoute>
            ),
          },
          {
            path: "all-student-data",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <StudentAdminData />
              </ProtectedRoute>
            ),
          },
          {
            path: "new-admission",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <NewAdmission />
              </ProtectedRoute>
            ),
          },
          {
            path: "bulk-update",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <BulkUpdate />
              </ProtectedRoute>
            ),
          },
          {
            path: "issue-tc",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <IssueTC />
              </ProtectedRoute>
            ),
          },
          {
            path: "student-scholarship",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <StudentScholarship />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "exams-management",
        element: <ExamsManagementRoot />,
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <ExamsManagementHome />
              </ProtectedRoute>
            ),
          },
          {
            path: "add-exams",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <AddExams />
              </ProtectedRoute>
            ),
          },
          {
            path: "schedule-exam",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <ScheduleExam />
              </ProtectedRoute>
            ),
          },
          {
            path: "get-admit-card",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <GetAdmitCard />
              </ProtectedRoute>
            ),
          },
          // {
          //   path: "manage-attendance",
          //   element: (
          //     <ProtectedRoute
          //       allowedRoles={[
          //         "admin",
          //         "teacher",
          //         "student",
          //         "feesadmin",
          //         "schooladmin",
          //       ]}
          //     >
          //       <ManageAttendance />
          //     </ProtectedRoute>
          //   ),
          // },
          {
            path: "manage-marks",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <ManageMarks />
              </ProtectedRoute>
            ),
          },
          {
            path: "generate-report-card",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <GenerateReportCard />
              </ProtectedRoute>
            ),
          },
        ],
      },
      {
        path: "staff-management",
        element: <StaffManagementRoot />,
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <StaffManagementHome />
              </ProtectedRoute>
            ),
          },
          {
            path: "all-teacher-data",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <TeachersAdminData />
              </ProtectedRoute>
            ),
          },
          {
            path: "teacher-class-assignment",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <TeacherSubjectAssignment />
              </ProtectedRoute>
            ),
          },
        ],
      },

      {
        path: "fees-details",
        element: (
          <ProtectedRoute
            allowedRoles={[
              "admin",
              "teacher",
              "student",
              "feesadmin",
              "schooladmin",
            ]}
          >
            <FeePaymentsScreen />
          </ProtectedRoute>
        ),
      },
      {
        path: "controls-settings",
        element: <Root />,
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <ControlSettingsHome />
              </ProtectedRoute>
            ),
          },
          {
            path: "manage-subjects",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <ManageSubjects />
              </ProtectedRoute>
            ),
          },

          {
            path: "manage-fee-heads",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <ManageFeeHeads />
              </ProtectedRoute>
            ),
          },
          {
            path: "manage-transport",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <ManageTransport />
              </ProtectedRoute>
            ),
          },
        ],
      },

      {
        path: "report-center",
        element: <ReportCenterRoot />,
        children: [
          {
            index: true,
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <ReportCenterHome />
              </ProtectedRoute>
            ),
          },
          {
            path: "fee-collection",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <FeeCollection />
              </ProtectedRoute>
            ),
          },
          {
            path: "fee-dues",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <FeeDues />
              </ProtectedRoute>
            ),
          },
          {
            path: "class-section-strength",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <ClassSectionStrength />
              </ProtectedRoute>
            ),
          },
          {
            path: "transport-report",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <TransportReport />
              </ProtectedRoute>
            ),
          },
          {
            path: "bonafide-report",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <BonafideReport />
              </ProtectedRoute>
            ),
          },
          {
            path: "ward-category-report",
            element: (
              <ProtectedRoute
                allowedRoles={[
                  "admin",
                  "teacher",
                  "student",
                  "feesadmin",
                  "schooladmin",
                ]}
              >
                <WardCategoryReport />
              </ProtectedRoute>
            ),
          },
        ],
      },
    ],
  },
]);
const Router = () => {
  return (
    <Suspense fallback={null}>
      <Styles />
      <RouterProvider router={myRouter} />
    </Suspense>
  );
};

export default Router;
